import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, Transition, Tab } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { Toaster } from "./ui/sonner";
import {
  Pencil,
  Check,
  X,
  Mail,
  Phone,
  Linkedin,
  Github,
  Twitter,
  Instagram,
  Globe,
  FileText,
  User,
  GraduationCap,
  Briefcase,
  Eye,
  TrendingUp,
  Flame,
  Users,
  Calendar,
  Download,
  Star,
  Share2,
  UserPlus,
  Trash2,
  Upload,
} from "lucide-react";
import { handleFileUpload } from "../scripts/S3";
import {
  saveProfileChanges,
  degreeTypeOptions,
  monthsOfYear,
} from "../scripts/Profile";
import ImageCropModal from "./ImageCropModal";
import ContactDetailModal from "./ContactDetailModal";
import GoogleReviewFinderModal from "./profileForm/GoogleReviewFinderModal";
import GiftDashboard from "../pages/Gift/GiftDashboard";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function ProfileDashboard({
  profile,
  setProfile,
  currentUser,
  streaksData,
}) {
  const [editedProfile, setEditedProfile] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [showReviewFinder, setShowReviewFinder] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [bannerCropModalOpen, setBannerCropModalOpen] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState(null);
  const [tempBannerSrc, setTempBannerSrc] = useState(null);

  // If this is a gift card, render the Gift Dashboard instead
  if (profile.cardType === "gift") {
    return <GiftDashboard cardId={profile.cardId} />;
  }

  useEffect(() => {
    resetEditedProfile();
  }, [profile]);

  const resetEditedProfile = () => {
    setEditedProfile({ ...profile });
  };

  const updateField = (field, value) => {
    setEditedProfile({ ...editedProfile, [field]: value });
    // Only mark as editing if the value actually changed from the original
    if (!isEditing && profile[field] !== value) {
      setIsEditing(true);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);

    // Extract ONLY the editable fields, don't send MongoDB internals
    const profileFieldsToUpdate = {
      firstName: editedProfile.firstName,
      lastName: editedProfile.lastName,
      email: editedProfile.email,
      phone: editedProfile.phone,
      college: editedProfile.college,
      linkedIn: editedProfile.linkedIn,
      x: editedProfile.x,
      instagram: editedProfile.instagram,
      github: editedProfile.github,
      portfolio: editedProfile.portfolio,
      resume: editedProfile.resume,
      additional: editedProfile.additional,
      headline: editedProfile.headline,
      degreeType: editedProfile.degreeType,
      major: editedProfile.major,
      collegeStartYear: editedProfile.collegeStartYear,
      collegeStartMonth: editedProfile.collegeStartMonth,
      collegeEndYear: editedProfile.collegeEndYear,
      collegeEndMonth: editedProfile.collegeEndMonth,
      skills: editedProfile.skills,
      skills: editedProfile.skills,
      imageUrl: editedProfile.imageUrl,
      bannerUrl: editedProfile.bannerUrl,
      // Business fields
      businessName: editedProfile.businessName,
      googleReviewLink: editedProfile.googleReviewLink,
      businessPurpose: editedProfile.businessPurpose,
      cardType: editedProfile.cardType,
      facebook: editedProfile.facebook,
      tiktok: editedProfile.tiktok,
      leadCapture: editedProfile.leadCapture,
      socialMediaConfig: editedProfile.socialMediaConfig,
    };

    try {
      const response = await saveProfileChanges(
        profileFieldsToUpdate,
        currentUser
      );

      if (response.status === 200) {
        // Update the full profile with the edited fields merged in
        setProfile({ ...profile, ...profileFieldsToUpdate });
        setIsEditing(false);
      }
    } catch (error) {
      console.error("=== DASHBOARD: handleSave ERROR ===");
      console.error("Error saving profile:", error);
      console.error("Error response:", error.response?.data);
      toast.error("Failed to save changes. Please try again.");
    }
    setIsSaving(false);
  };

  const handleCancel = () => {
    resetEditedProfile();
    setIsEditing(false);
    setEditingField(null);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setTempImageSrc(reader.result);
        setCropModalOpen(true);
      });
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = async (croppedBlob) => {
    try {
      // Convert blob to file
      const croppedFile = new File([croppedBlob], "profile-image.jpg", {
        type: "image/jpeg",
      });
      const url = await handleFileUpload(croppedFile);
      updateField("imageUrl", url);
    } catch (error) {
      console.error("Error uploading cropped image:", error);
      toast.error("Failed to upload image. Please try again.");
    }
  };

  const handleBannerSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setTempBannerSrc(reader.result);
        setBannerCropModalOpen(true);
      });
      reader.readAsDataURL(file);
    }
  };

  const handleBannerCropComplete = async (croppedBlob) => {
    try {
      // Convert blob to file
      const croppedFile = new File([croppedBlob], "banner-image.jpg", {
        type: "image/jpeg",
      });
      const url = await handleFileUpload(croppedFile);
      updateField("bannerUrl", url);
    } catch (error) {
      console.error("Error uploading cropped banner:", error);
      toast.error("Failed to upload banner. Please try again.");
    }
  };

  const tabs = [
    { name: "Edit Profile", icon: User },
    { name: "Analytics", icon: TrendingUp },
    { name: "Contacts", icon: Users },
  ];

  return (
    <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <Toaster position="top-center" richColors />
      {/* Image Crop Modal */}
      <ImageCropModal
        open={cropModalOpen}
        setOpen={setCropModalOpen}
        imageSrc={tempImageSrc}
        onCropComplete={handleCropComplete}
        aspect={1}
      />

      {/* Banner Crop Modal */}
      <ImageCropModal
        open={bannerCropModalOpen}
        setOpen={setBannerCropModalOpen}
        imageSrc={tempBannerSrc}
        onCropComplete={handleBannerCropComplete}
        aspect={3}
      />

      {/* Google Review Finder Modal */}
      <GoogleReviewFinderModal
        open={showReviewFinder}
        setOpen={setShowReviewFinder}
        onSelectLink={(link) => updateField("googleReviewLink", link)}
        currentValue={editedProfile.googleReviewLink}
        businessName={editedProfile.businessName}
      />

      {/* Sticky Save/Cancel Bar */}
      <AnimatePresence>
        {isEditing && (
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm"
          >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                <span className="text-xs sm:text-sm font-medium text-gray-700">
                  Unsaved changes
                </span>
              </div>
              <div className="flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCancel}
                  className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      />
                      <span className="hidden sm:inline">Saving...</span>
                    </>
                  ) : (
                    <>
                      <Check size={14} />
                      <span className="hidden sm:inline">Save Changes</span>
                      <span className="sm:hidden">Save</span>
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabbed Interface with Sticky Header */}
      <Tab.Group>
        <div className="sticky top-0 z-40 bg-gray-50/95 backdrop-blur-sm pt-4 pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 transition-all duration-200 border-b border-gray-200/50">
          {/* Profile Preview Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-4 relative"
          >
            <div className="absolute top-4 right-4">
              <button
                onClick={() => window.open(`/profile/${profile.cardId}`, "_blank")}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                title="Preview Public Profile"
              >
                <Eye size={16} />
                <span className="hidden sm:inline">Preview</span>
              </button>
            </div>
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
              <div className="relative group flex-shrink-0">
                {editedProfile.imageUrl ? (
                  <img
                    src={editedProfile.imageUrl}
                    alt={`${editedProfile.firstName} ${editedProfile.lastName}`}
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-blue-500 flex items-center justify-center text-white text-2xl sm:text-3xl font-bold">
                    {editedProfile.firstName?.[0]}
                    {editedProfile.lastName?.[0]}
                  </div>
                )}
                <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-50 rounded-full cursor-pointer transition-all group-hover:opacity-100 opacity-0">
                  <Pencil className="text-white" size={16} />
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageSelect}
                  />
                </label>
              </div>
              <div className="flex-1 text-center sm:text-left w-full">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {editedProfile.cardType === "business"
                    ? editedProfile.businessName || "Business Name"
                    : `${editedProfile.firstName} ${editedProfile.lastName}`}
                </h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base">
                  {editedProfile.headline || "Add a headline"}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-2 sm:gap-4 mt-3 text-xs sm:text-sm text-gray-500">
                  {editedProfile.email && (
                    <span className="flex items-center gap-1">
                      <Mail size={14} />
                      <span className="truncate max-w-[200px]">
                        {editedProfile.email}
                      </span>
                    </span>
                  )}
                  {editedProfile.phone && (
                    <span className="flex items-center gap-1">
                      <Phone size={14} />
                      {editedProfile.phone}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <Tab
                  key={tab.name}
                  className={({ selected }) =>
                    classNames(
                      "w-full rounded-lg py-2.5 px-2 sm:px-4 text-xs sm:text-sm font-medium leading-5 transition-all flex items-center justify-center gap-2",
                      "focus:outline-none focus:ring-2 ring-offset-2 ring-offset-blue-400 ring-white ring-opacity-60",
                      selected
                        ? "bg-white text-blue-700 shadow"
                        : "text-gray-600 hover:bg-white/[0.12] hover:text-gray-800"
                    )
                  }
                >
                  <IconComponent size={16} className="flex-shrink-0" />
                  <span className="hidden sm:inline">{tab.name}</span>
                </Tab>
              );
            })}
          </Tab.List>
        </div>

        <Tab.Panels className="mt-6">
          {/* Edit Profile Tab */}
          <Tab.Panel>
            <EditProfileTab
              editedProfile={editedProfile}
              updateField={updateField}
              editingField={editingField}
              setEditingField={setEditingField}
              setShowReviewFinder={setShowReviewFinder}
              handleSave={handleSave}
              handleBannerSelect={handleBannerSelect}
            />
          </Tab.Panel>

          {/* Analytics Tab */}
          <Tab.Panel>
            <AnalyticsTab streaksData={streaksData} profile={profile} />
          </Tab.Panel>

          {/* Contacts Tab */}
          <Tab.Panel>
            <ContactsTab currentUser={currentUser} />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}

// Edit Profile Tab Component
function EditProfileTab({
  editedProfile,
  updateField,
  editingField,
  setEditingField,
  setShowReviewFinder,
  handleSave,
  handleBannerSelect,
}) {
  const isBusiness = editedProfile.cardType === "business";
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);

  const handleModeClick = (modeId) => {
    if (editedProfile.businessPurpose === modeId) return;
    setPendingMode(modeId);
    setConfirmModalOpen(true);
  };

  const confirmSwitch = () => {
    updateField("businessPurpose", pendingMode);
    setConfirmModalOpen(false);
    setPendingMode(null);
  };

  const modes = [
    {
      id: "google-reviews",
      label: "Get Reviews",
      icon: Star,
      description: "Direct customers to your Google Reviews page",
    },
    {
      id: "social-media",
      label: "Social Hub",
      icon: Share2,
      description: "Showcase all your social media profiles",
    },
    {
      id: "leads",
      label: "Capture Leads",
      icon: UserPlus,
      description: "Collect names and emails from potential clients",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Business Mode Selector - Top Priority for Business */}
      {isBusiness && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Card Mode</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {modes.map((mode) => {
              const Icon = mode.icon;
              const isSelected = editedProfile.businessPurpose === mode.id;
              return (
                <button
                  key={mode.id}
                  onClick={() => handleModeClick(mode.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all relative ${
                    isSelected
                      ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-3 right-3 text-blue-500">
                      <Check size={16} />
                    </div>
                  )}
                  <Icon
                    className={`mb-3 h-6 w-6 ${
                      isSelected ? "text-blue-600" : "text-gray-500"
                    }`}
                  />
                  <div className="font-semibold text-gray-900 text-sm">
                    {mode.label}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 leading-snug">
                    {mode.description}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Dynamic Configuration based on Mode */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            {editedProfile.businessPurpose === "google-reviews" && (
              <div className="space-y-6">
                <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Star size={16} className="text-yellow-500" />
                  Review Settings
                </h4>

                {/* Google Review Link Section */}
                <div className="space-y-3 pb-4 border-b border-gray-200">
                  <EditableField
                    label="Google Review Link"
                    value={editedProfile.googleReviewLink}
                    onChange={(val) => updateField("googleReviewLink", val)}
                    isEditing={editingField === "googleReviewLink"}
                    setEditing={(val) => val ? setEditingField("googleReviewLink") : setEditingField(null)}
                    onSave={handleSave}
                    placeholder="Paste your Google Reviews link here"
                  />
                  <button
                    type="button"
                    onClick={() => setShowReviewFinder(true)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                  >
                    <MagnifyingGlassIcon className="h-4 w-4" />
                    Find my review link
                  </button>
                 
                </div>

                {/* Brand Color Section */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Brand Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={editedProfile.socialMediaConfig?.brandColor || "#2563EB"}
                      onChange={(e) =>
                        updateField("socialMediaConfig", {
                          ...editedProfile.socialMediaConfig,
                          brandColor: e.target.value,
                        })
                      }
                      className="h-10 w-20 rounded-lg border border-gray-300 cursor-pointer"
                    />
                    <span className="text-sm text-gray-600 font-mono">
                      {editedProfile.socialMediaConfig?.brandColor || "#2563EB"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    This color will be used for the gradient header on your review page.
                  </p>
                </div>
              </div>
            )}

            {editedProfile.businessPurpose === "social-media" && (
              <div className="space-y-6">
                <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Share2 size={16} className="text-blue-500" />
                  Social Hub Customization
                </h4>

                {/* Brand Color */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Brand Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={editedProfile.socialMediaConfig?.brandColor || "#2563EB"}
                      onChange={(e) =>
                        updateField("socialMediaConfig", {
                          ...editedProfile.socialMediaConfig,
                          brandColor: e.target.value,
                        })
                      }
                      className="h-10 w-20 rounded-lg border border-gray-300 cursor-pointer"
                    />
                    <span className="text-sm text-gray-600 font-mono">
                      {editedProfile.socialMediaConfig?.brandColor || "#2563EB"}
                    </span>
                  </div>
                </div>

                {/* Banner Image Upload */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Banner Image
                  </label>
                  {editedProfile.socialMediaConfig?.bannerImage ? (
                    <div className="relative w-full h-32 rounded-lg overflow-hidden border border-gray-200">
                      <img
                        src={editedProfile.socialMediaConfig.bannerImage}
                        alt="Banner"
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() =>
                          updateField("socialMediaConfig", {
                            ...editedProfile.socialMediaConfig,
                            bannerImage: "",
                          })
                        }
                        className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                      <Upload size={24} className="text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Click to upload banner</span>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            // For now, create a local URL - you'll need to implement actual upload
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              updateField("socialMediaConfig", {
                                ...editedProfile.socialMediaConfig,
                                bannerImage: reader.result,
                              });
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
            )}

            {editedProfile.businessPurpose === "leads" && (
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <UserPlus size={16} className="text-green-500" />
                  Lead Capture Settings
                </h4>

                {/* Tagline */}
                <EditableField
                  label="Form Tagline"
                  value={
                    editedProfile.leadCapture?.tagline ||
                    "Join our mailing list"
                  }
                  onChange={(val) =>
                    updateField("leadCapture", {
                      ...editedProfile.leadCapture,
                      tagline: val,
                    })
                  }
                  isEditing={editingField === "tagline"}
                  setEditing={(val) => val ? setEditingField("tagline") : setEditingField(null)}
                  onSave={handleSave}
                  placeholder="e.g., Join our mailing list"
                />

                {/* Brand Color */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Brand Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={editedProfile.socialMediaConfig?.brandColor || "#2563EB"}
                      onChange={(e) =>
                        updateField("socialMediaConfig", {
                          ...editedProfile.socialMediaConfig,
                          brandColor: e.target.value,
                        })
                      }
                      className="h-10 w-20 rounded-lg border border-gray-300 cursor-pointer"
                    />
                    <span className="text-sm text-gray-600 font-mono">
                      {editedProfile.socialMediaConfig?.brandColor || "#2563EB"}
                    </span>
                  </div>
                </div>

                {/* Phone Toggle */}
                <div className="flex items-center justify-between py-2">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      Collect Phone Number
                    </div>
                    <div className="text-xs text-gray-500">
                      Add an optional phone number field
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      updateField("leadCapture", {
                        ...editedProfile.leadCapture,
                        phoneEnabled:
                          !editedProfile.leadCapture?.phoneEnabled,
                      })
                    }
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      editedProfile.leadCapture?.phoneEnabled
                        ? "bg-blue-600"
                        : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        editedProfile.leadCapture?.phoneEnabled
                          ? "translate-x-5"
                          : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>

                {/* Custom Questions */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-gray-900">
                      Custom Questions
                      <span className="text-xs text-gray-400 font-normal ml-2">
                        ({(editedProfile.leadCapture?.questions || []).length}
                        /3)
                      </span>
                    </div>
                    {(editedProfile.leadCapture?.questions || []).length <
                      3 && (
                      <button
                        onClick={() => {
                          const questions =
                            editedProfile.leadCapture?.questions || [];
                          updateField("leadCapture", {
                            ...editedProfile.leadCapture,
                            questions: [
                              ...questions,
                              {
                                id: Date.now().toString(),
                                text: "",
                                type: "text",
                              },
                            ],
                          });
                        }}
                        className="text-xs text-blue-600 font-medium hover:text-blue-700"
                      >
                        + Add Question
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    {(editedProfile.leadCapture?.questions || []).map(
                      (q, idx) => (
                        <div key={q.id} className="flex gap-2">
                          <input
                            type="text"
                            value={q.text}
                            onChange={(e) => {
                              const questions = [
                                ...(editedProfile.leadCapture?.questions ||
                                  []),
                              ];
                              questions[idx] = {
                                ...questions[idx],
                                text: e.target.value,
                              };
                              updateField("leadCapture", {
                                ...editedProfile.leadCapture,
                                questions,
                              });
                            }}
                            placeholder="e.g., What is your budget?"
                            maxLength={100}
                            className="flex-1 text-sm border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                          />
                          <button
                            onClick={() => {
                              const questions = (
                                editedProfile.leadCapture?.questions || []
                              ).filter((_, i) => i !== idx);
                              updateField("leadCapture", {
                                ...editedProfile.leadCapture,
                                questions,
                              });
                            }}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )
                    )}
                    {(!editedProfile.leadCapture?.questions ||
                      editedProfile.leadCapture.questions.length === 0) && (
                      <div className="text-xs text-gray-500 italic">
                        No custom questions added.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <AnimatePresence>
        {confirmModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Switch Mode?
              </h3>
              <p className="text-gray-600 mb-6 text-sm">
                Changing modes will update your card's layout and settings. Are
                you sure you want to switch?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setConfirmModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSwitch}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Switch
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Profile Appearance */}
      <Section title="Profile Appearance">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Banner Image
          </label>
          {editedProfile.bannerUrl ? (
            <div className="relative w-full h-32 rounded-lg overflow-hidden border border-gray-200">
              <img
                src={editedProfile.bannerUrl}
                alt="Banner"
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => updateField("bannerUrl", "")}
                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
              <Upload size={24} className="text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">Click to upload banner</span>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleBannerSelect}
              />
            </label>
          )}
          <p className="text-xs text-gray-500">
            Recommended size: 1500x500px. This will replace the default background pattern.
          </p>
        </div>
      </Section>

      {/* Basic/Business Info Section */}
      <Section title={isBusiness ? "Business Information" : "Basic Information"}>
        {isBusiness ? (
          <EditableField
            label="Business Name"
            value={editedProfile.businessName}
            onChange={(val) => updateField("businessName", val)}
            isEditing={editingField === "businessName"}
            setEditing={(val) => val ? setEditingField("businessName") : setEditingField(null)}
            onSave={handleSave}
            placeholder="Your Business Name"
          />
        ) : (
          <>
            <EditableField
              label="First Name"
              value={editedProfile.firstName}
              onChange={(val) => updateField("firstName", val)}
              isEditing={editingField === "firstName"}
              setEditing={(val) => val ? setEditingField("firstName") : setEditingField(null)}
              onSave={handleSave}
            />
            <EditableField
              label="Last Name"
              value={editedProfile.lastName}
              onChange={(val) => updateField("lastName", val)}
              isEditing={editingField === "lastName"}
              setEditing={(val) => val ? setEditingField("lastName") : setEditingField(null)}
              onSave={handleSave}
            />
          </>
        )}
        <EditableField
          label={isBusiness ? "Tagline" : "Headline"}
          value={editedProfile.headline}
          onChange={(val) => updateField("headline", val)}
          isEditing={editingField === "headline"}
          setEditing={(val) => val ? setEditingField("headline") : setEditingField(null)}
          onSave={handleSave}
          placeholder={
            isBusiness
              ? "e.g., Best Coffee in Town"
              : "Ex-Microsoft intern, incoming @ Google"
          }
        />
      </Section>

      {/* Business Details - Removed (Integrated into Mode Selector) */}

      {/* Contact Information */}
      <Section title="Contact Information">
        <EditableField
          label="Email"
          value={editedProfile.email}
          onChange={(val) => updateField("email", val)}
          isEditing={editingField === "email"}
          setEditing={(val) => val ? setEditingField("email") : setEditingField(null)}
          onSave={handleSave}
          type="email"
        />
        <EditableField
          label="Phone"
          value={editedProfile.phone}
          onChange={(val) => updateField("phone", val)}
          isEditing={editingField === "phone"}
          setEditing={(val) => val ? setEditingField("phone") : setEditingField(null)}
          onSave={handleSave}
          type="tel"
        />
      </Section>

      {/* Education - Only for Individual */}
      {!isBusiness && (
        <Section title="Education">
          <EditableField
            label="School"
            value={editedProfile.college}
            onChange={(val) => updateField("college", val)}
            isEditing={editingField === "college"}
            setEditing={(val) => val ? setEditingField("college") : setEditingField(null)}
            onSave={handleSave}
          />
          <EditableField
            label="Major"
            value={editedProfile.major}
            onChange={(val) => updateField("major", val)}
            isEditing={editingField === "major"}
            setEditing={(val) => val ? setEditingField("major") : setEditingField(null)}
            onSave={handleSave}
          />
          <EditableField
            label="Degree"
            value={editedProfile.degreeType}
            onChange={(val) => updateField("degreeType", val)}
            isEditing={editingField === "degreeType"}
            setEditing={(val) => val ? setEditingField("degreeType") : setEditingField(null)}
            onSave={handleSave}
            isSelect={true}
            options={degreeTypeOptions}
          />
        </Section>
      )}

      {/* Social Links */}
      <Section title="Social Links">
        <EditableField
          label="LinkedIn"
          value={editedProfile.linkedIn}
          onChange={(val) => updateField("linkedIn", val)}
          isEditing={editingField === "linkedIn"}
          setEditing={(val) => val ? setEditingField("linkedIn") : setEditingField(null)}
          onSave={handleSave}
          placeholder="e.g., linkedin.com/in/yourprofile"
        />
        <EditableField
          label="GitHub"
          value={editedProfile.github}
          onChange={(val) => updateField("github", val)}
          isEditing={editingField === "github"}
          setEditing={(val) => val ? setEditingField("github") : setEditingField(null)}
          onSave={handleSave}
          placeholder="e.g., github.com/yourprofile"
        />
        <EditableField
          label="X (Twitter)"
          value={editedProfile.x}
          onChange={(val) => updateField("x", val)}
          isEditing={editingField === "x"}
          setEditing={(val) => val ? setEditingField("x") : setEditingField(null)}
          onSave={handleSave}
          placeholder="e.g., x.com/yourhandle"
        />
        <EditableField
          label="Instagram"
          value={editedProfile.instagram}
          onChange={(val) => updateField("instagram", val)}
          isEditing={editingField === "instagram"}
          setEditing={(val) => val ? setEditingField("instagram") : setEditingField(null)}
          onSave={handleSave}
          placeholder="e.g., instagram.com/yourhandle"
        />
        <EditableField
          label="Facebook"
          value={editedProfile.facebook}
          onChange={(val) => updateField("facebook", val)}
          isEditing={editingField === "facebook"}
          setEditing={(val) => val ? setEditingField("facebook") : setEditingField(null)}
          onSave={handleSave}
          placeholder="e.g., facebook.com/yourpage"
        />
        <EditableField
          label="TikTok"
          value={editedProfile.tiktok}
          onChange={(val) => updateField("tiktok", val)}
          isEditing={editingField === "tiktok"}
          setEditing={(val) => val ? setEditingField("tiktok") : setEditingField(null)}
          onSave={handleSave}
          placeholder="e.g., tiktok.com/@yourhandle"
        />
      </Section>

      {/* Portfolio & Resume */}
      <Section title={isBusiness ? "Links & Resources" : "Portfolio & Resume"}>
        <EditableField
          label="Website"
          value={editedProfile.portfolio}
          onChange={(val) => updateField("portfolio", val)}
          isEditing={editingField === "portfolio"}
          setEditing={(val) => val ? setEditingField("portfolio") : setEditingField(null)}
          onSave={handleSave}
          placeholder="yourwebsite.com"
        />
        {!isBusiness && (
          <EditableField
            label="Resume"
            value={editedProfile.resume}
            onChange={(val) => updateField("resume", val)}
            isEditing={editingField === "resume"}
            setEditing={(val) => val ? setEditingField("resume") : setEditingField(null)}
            onSave={handleSave}
            placeholder="Link to your resume"
          />
        )}
      </Section>
    </div>
  );
}

// Analytics Tab Component
function AnalyticsTab({ streaksData, profile }) {
  const totalVisits = streaksData.reduce((sum, day) => sum + day.count, 0);
  const avgVisits =
    streaksData.length > 0 ? (totalVisits / streaksData.length).toFixed(1) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard title="Total Views" value={totalVisits} icon={Eye} />
        <StatCard title="Avg/Day" value={avgVisits} icon={TrendingUp} />
        <StatCard
          title="Last 7 Days"
          value={streaksData.slice(-7).reduce((sum, day) => sum + day.count, 0)}
          icon={Flame}
        />
      </div>

      {/* Visits Chart */}
      {streaksData.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-900">
              Recent Activity
            </h3>
          </div>
          <div className="p-4 sm:p-6 space-y-2">
            {streaksData
              .slice(-14)
              .reverse()
              .map((day, index) => (
                <div key={index} className="flex items-center gap-4">
                  <span className="text-sm text-gray-600 w-24">
                    {new Date(day.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <div className="flex-1">
                    <div className="bg-gray-100 rounded-full h-8 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{
                          width: `${
                            (day.count /
                              Math.max(...streaksData.map((d) => d.count))) *
                            100
                          }%`,
                        }}
                        transition={{ duration: 0.5, delay: index * 0.05 }}
                        className="bg-gradient-to-r from-blue-500 to-blue-600 h-full flex items-center justify-end pr-2"
                      >
                        {day.count > 0 && (
                          <span className="text-xs font-medium text-white">
                            {day.count}
                          </span>
                        )}
                      </motion.div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Contacts Tab Component
function ContactsTab({ currentUser }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Google-style color palette for avatars
  const getAvatarColor = (name) => {
    const colors = [
      "bg-blue-500",
      "bg-indigo-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-red-500",
      "bg-orange-500",
      "bg-yellow-500",
      "bg-green-500",
      "bg-teal-500",
      "bg-cyan-500",
    ];

    // Use name to deterministically pick a color
    const charCode = name?.charCodeAt(0) || 0;
    return colors[charCode % colors.length];
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      if (!currentUser?.email) {
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_BACKEND_URL}get_contacts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: currentUser.email }),
        }
      );

      if (response.ok) {
        const contactsData = await response.json();
        setContacts(contactsData || []);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactUpdate = (updatedContact) => {
    setContacts(
      contacts.map((c) =>
        c.email === updatedContact.email ? updatedContact : c
      )
    );
  };

  return (
    <div className="space-y-4">
      {/* Contact Detail Modal */}
      <ContactDetailModal
        open={detailModalOpen}
        setOpen={setDetailModalOpen}
        contact={selectedContact}
        onUpdate={handleContactUpdate}
        currentUserEmail={currentUser?.email}
      />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900">Saved Contacts</h3>
          {contacts.length > 0 && (
            <button
              onClick={() => {
                // Create CSV content
                const headers = ["Name", "Email", "Phone", "Location", "Date Added", "Mutual"];
                const rows = contacts.map(contact => [
                  contact.name || "",
                  contact.email || "",
                  contact.phone || "",
                  contact.location?.city || "",
                  contact.timestamp ? new Date(contact.timestamp).toLocaleDateString() : "",
                  contact.mutual ? "Yes" : "No"
                ]);
                
                // Convert to CSV format
                const csvContent = [
                  headers.join(","),
                  ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
                ].join("\n");
                
                // Create and download file
                const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `contacts_${new Date().toISOString().split('T')[0]}.csv`;
                link.click();
                window.URL.revokeObjectURL(url);
              }}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
              title="Export all contacts as CSV"
            >
              <Download size={14} />
              Export CSV
            </button>
          )}
        </div>
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <Users size={32} className="text-gray-400" />
              </div>
              <p className="text-gray-500 font-medium">No contacts yet</p>
              <p className="text-sm text-gray-400 mt-2">
                People who exchange contact with you will appear here
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {contacts.map((contact, index) => (
                <div
                  key={index}
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    setSelectedContact(contact);
                    setDetailModalOpen(true);
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 ${getAvatarColor(
                        contact.name
                      )}`}
                    >
                      {contact.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm sm:text-base">
                        {contact.name || "No name"}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500 truncate">
                        {contact.email || "No email"}
                      </div>
                      {contact.phone && (
                        <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                          <Phone size={12} />
                          {contact.phone}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {contact.mutual && (
                        <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full font-medium border border-green-200">
                          Mutual
                        </span>
                      )}
                      {contact.timestamp && (
                        <span className="text-xs text-gray-400">
                          {new Date(contact.timestamp).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                            }
                          )}
                        </span>
                      )}
                      {contact.location?.city && (
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Globe size={10} />
                          {contact.location.city}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex items-center justify-end gap-1 mt-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Create vCard
                        const vCard = `BEGIN:VCARD
VERSION:3.0
FN:${contact.name}
EMAIL:${contact.email}
TEL:${contact.phone || ""}
END:VCARD`;
                        const blob = new Blob([vCard], { type: "text/vcard" });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${contact.name?.replace(/\s/g, "_")}.vcf`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                      }}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Save to phone"
                    >
                      <Download size={18} />
                    </button>
                    {contact.email && (
                      <a
                        href={`mailto:${contact.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Send email"
                      >
                        <Mail size={18} />
                      </a>
                    )}
                    {contact.phone && (
                      <a
                        href={`tel:${contact.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Call"
                      >
                        <Phone size={18} />
                      </a>
                    )}
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (
                          window.confirm(
                            `Are you sure you want to delete ${contact.name} from your contacts?`
                          )
                        ) {
                          try {
                            const response = await fetch(
                              `${process.env.REACT_APP_PUBLIC_BACKEND_URL}delete_contact`,
                              {
                                method: "POST",
                                headers: {
                                  "Content-Type": "application/json",
                                },
                                body: JSON.stringify({
                                  email: currentUser.email,
                                  contactEmail: contact.email,
                                }),
                              }
                            );

                            if (response.ok) {
                              setContacts(
                                contacts.filter((c) => c.email !== contact.email)
                              );
                            } else {
                              toast.error("Failed to delete contact");
                            }
                          } catch (error) {
                            console.error("Error deleting contact:", error);
                            toast.error("Error deleting contact");
                          }
                        }
                      }}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete contact"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Reusable Section Component (Google-style: clean, minimal)
function Section({ title, children }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-medium text-gray-900">{title}</h3>
      </div>
      <div className="p-4 sm:p-6 space-y-1">{children}</div>
    </div>
  );
}

// Stat Card Component (Google-style: clean cards with subtle borders)
function StatCard({ title, value, icon }) {
  const IconComponent = icon;
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 hover:border-gray-300 transition-colors">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
          <IconComponent size={16} className="text-blue-600" />
        </div>
        <p className="text-xs sm:text-sm font-medium text-gray-600">{title}</p>
      </div>
      <p className="text-2xl sm:text-3xl font-normal text-gray-900 ml-11">
        {value}
      </p>
    </div>
  );
}

// Editable Field Component (Google-style: clean list items)
function EditableField({
  label,
  value,
  onChange,
  isEditing,
  setEditing,
  placeholder,
  type = "text",
  isSelect = false,
  options = [],
  onSave,
}) {
  const [localValue, setLocalValue] = useState(value || "");
  const [isSaving, setIsSaving] = useState(false);
  const fieldRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const handleChange = useCallback((newValue) => {
    setLocalValue(newValue);
    // Immediately notify parent of the change for real-time unsaved detection
    onChange(newValue);
  }, [onChange]);

  const handleBlur = useCallback(() => {
    // Just close editing, value already updated via handleChange
    setEditing(null);
  }, [setEditing]);

  // Handle click outside to close editing
  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event) => {
      // Check if click is outside the entire container (input + buttons)
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleBlur();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, handleBlur]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Trigger save on Enter
      handleCheckClick();
    } else if (e.key === "Escape") {
      setLocalValue(value || "");
      setEditing(null);
    }
  };

  const handleCheckClick = async () => {
    setIsSaving(true);
    handleChange(localValue);
    
    // Minimum spinner duration for better UX
    const minDelay = new Promise(resolve => setTimeout(resolve, 600));
    
    try {
      if (onSave) {
        await Promise.all([onSave(), minDelay]);
      } else {
        await minDelay;
      }
    } catch (error) {
      console.error("Save failed:", error);
      // Optional: keep editing open if save fails
    } finally {
      setIsSaving(false);
      setEditing(null);
    }
  };

  return (
    <div className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-100 last:border-b-0 gap-2">
      <label className="text-sm font-medium text-gray-700 min-w-[140px]">
        {label}
      </label>
      <div className="flex-1 sm:max-w-md">
        {isEditing ? (
          <div ref={containerRef} className="flex items-center gap-2">
            {isSelect ? (
              <select
                ref={fieldRef}
                value={localValue}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 px-3 py-1.5 text-sm border border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              >
                {options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <input
                ref={fieldRef}
                type={type}
                value={localValue}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="flex-1 px-3 py-1.5 text-sm border border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            )}
            <button
              type="button"
              onClick={handleCheckClick}
              disabled={isSaving}
              className="p-1.5 text-green-600 hover:bg-green-50 rounded-md transition-all disabled:opacity-50"
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Check size={16} />
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setLocalValue(value || "");
                setEditing(null);
              }}
              disabled={isSaving}
              className="p-1.5 text-gray-400 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div
            onClick={() => setEditing(true)}
            className="group flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded-md px-3 py-1.5 transition-colors"
          >
            <span className="text-sm text-gray-900 truncate flex-1 mr-2">
              {value || (
                <span className="text-gray-400 italic">{placeholder}</span>
              )}
            </span>
            <Pencil
              size={14}
              className="text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0"
            />
          </div>
        )}
      </div>
    </div>
  );
}
